import { useCallback, useMemo, useState } from "react";
import { ApolloQueryResult, NetworkStatus, useApolloClient } from "@apollo/client";

import { useAppDispatch, useAppSelector } from "../../../Store";

import { PanelBasicQueryInfoInput } from "../FeedDataCalls/GQLCalls/getFeedComponentsCall";

import { gqlActions } from "../gqlData_Reducer";
import Graph_QL, { DynamicDataObk, GqlHook_Returns, StateDataObject } from "..";
import { noop } from "lodash";
import { filterByConnection } from "../filterByConnection";

import { AnimalItem } from "./AnimalDataSlices/AnimalSlice";
import { AnimalInfo, AnimalResponse, GET_ANIMALS_INFO } from "./GQLCalls/getAnimalCall";
import { FeedCurveDirectives } from "../FeedDataCalls/useFeedCurves";
import { CREATE_ANIMAL_INFO, CreateAnimalParameters, CreateAnimalsResponse } from "./GQLCalls/createAnimalInfoCall";
import { MAINTAIN_ANIMAL_INFO, MaintainAnimalsResponse, PanelMaintainAnimal } from "./GQLCalls/maintainAnimalInfoCall";
import { FilteredAnimalsResponse, GET_FILTERED_ANIMALS } from "./GQLCalls/getFilteredAnimalsCall";
import { QuickEntry } from "../../../Definitions";

interface AnimalInfoInput extends PanelBasicQueryInfoInput {
  idIdentifier?: "fk_locations";
}

interface AnimalDirectives {
  ifSkipConsumption?: boolean;
  ifSkipLastTenDaysConsumption?: boolean;
}
interface AnimalHelperFunctions {
  getAnimalAtLocation: (locationId: number, domain: string, getFull?: boolean) => AnimalInfo[];
  getQuickEntryAnimals: (domain: string, filters: QuickEntry[]) => Promise<ApolloQueryResult<FilteredAnimalsResponse>>;
  createAnimals: (newAnimals: CreateAnimalParameters[]) => void;
}

const camelToSnakeCase = (stringValue: string) => {
  // the table id for feed curve is with misspelled feedcurve in the database
  return stringValue === "feedCurveId" ? "feedcurve_id": stringValue.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

export const useAnimal = (connection: Graph_QL.GQLCons): GqlHook_Returns<AnimalItem, AnimalInfoInput & FeedCurveDirectives & AnimalDirectives> & AnimalHelperFunctions  => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const [, setCursorId] = useState(-1);

  const animalsState = useAppSelector(({graphQlData:{domain}}) => domain.animal);
  const {fullState} = useMemo(() => {
    const state = filterByConnection(animalsState, Graph_QL.ToArr(connection)) as Record<string, AnimalItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = {...data.data, domain:key}; 
        accumulator.data = accumulator.data.concat(value)
      }
      return accumulator
    },{fullState: state, data: []} as StateDataObject<AnimalItem>)
  },[connection, animalsState]);

  const getAnimalInfo = useCallback(async (input: AnimalInfoInput & FeedCurveDirectives & AnimalDirectives, specific?: Graph_QL.GQLCons) => {
    const specificConnection = specific ? specific : connection;
    const {ifFeedCurveBaseInfoOnly, ifFeedCurveInfo, ifSpreading, idIdentifier, ifSkipConsumption, ifSkipLastTenDaysConsumption, ...remainingInput} = input
    try {
      client.query<AnimalResponse>({query: GET_ANIMALS_INFO, variables: {...specificConnection.connection, input: remainingInput, idIdentifier: idIdentifier, ifFeedCurveBaseInfoOnly: ifFeedCurveBaseInfoOnly ?? true, ifFeedCurveInfo: ifFeedCurveInfo ?? false, ifSpreading: ifSpreading ?? false, ifSkipConsumption: ifSkipConsumption ?? false, ifSkipLastTenDaysConsumption: ifSkipLastTenDaysConsumption ?? true}, fetchPolicy: input.ids && input.ids?.length > 0 ? "network-only" : undefined})
        .then((e) => {
          if (e && e.data) {
            dispatch({type: String(gqlActions.domain.animals.maintained), payload: {con: specificConnection.connection, data: e.data.animal.info}});
            setCursorId(e.data.animal.info.at(-1)?.animal_id ?? -1)
          }
        }).catch((error) => {console.log(error)})
    } catch (error) {
        console.log(error);
      }
    },[client, connection, dispatch])

  const getAnimalAtLocation = useCallback((locationId: number, domain: string, getFull: boolean = false) => {
    // TODO CTO consider making this a db call so we know we have the correct animal and so we don't have to filter by serving it
    const animalState = fullState[domain]
    if (animalState !== undefined) {
      const animalsOfInterest = animalState.data.filter((animal) => animal.location_id === locationId);
      if (!getFull) {
        return animalsOfInterest;
      }
      if (animalsOfInterest[0] && animalsOfInterest[0]?.feedCurve?.milestones && animalsOfInterest[0]?.feedCurve?.components) {
        return animalsOfInterest;
      }
      if (animalsOfInterest[0]) {
        getAnimalInfo({ ids: [animalsOfInterest[0].animal_id], ifFeedCurveBaseInfoOnly: false })
      }
    } else {
      getAnimalInfo({
        ids: [locationId], idIdentifier: "fk_locations"
      })
    }
    return [];
    },[fullState, getAnimalInfo])
  
  const maintainEntries = useCallback(async (updatedAnimalsData?: Partial<DynamicDataObk<PanelMaintainAnimal[]>>, deletedAnimalsData?: Partial<DynamicDataObk<PanelMaintainAnimal[]>>, __?: Partial<DynamicDataObk<undefined>>, callback?: () => void) => {
    const combinedAnimalData = [...(updatedAnimalsData?.data ?? []), ...(deletedAnimalsData?.data ?? [])]
    try {
      client.mutate<MaintainAnimalsResponse>({mutation: MAINTAIN_ANIMAL_INFO, variables: {...connection.connection, animals: combinedAnimalData, ifFeedCurveBaseInfoOnly: true, ifFeedCurveInfo: false, ifSpreading: false, ifSkipConsumption: false, ifSkipLastTenDaysConsumption: true}})
        .then((response) => {
          if (response && response.data && response.data.animal.maintain.success ) {
            dispatch({ type: String(gqlActions.domain.animals.maintained), payload: { con: connection.connection, data: response.data.animal.maintain.updatedValues } })
            if (callback) {
              callback();
            }
          }
        }).catch((error) => {console.log(error)})
    } catch (error) {
      console.log(error);
    }
  },[client, connection.connection, dispatch])

  const createAnimals = useCallback(async (animals: CreateAnimalParameters[]) => {
    try {
      client.mutate<CreateAnimalsResponse>({mutation: CREATE_ANIMAL_INFO, variables: {...connection.connection, animals, ifFeedCurveBaseInfoOnly: true, ifFeedCurveInfo: false, ifSpreading: false, ifSkipConsumption: true, ifSkipLastTenDaysConsumption: true}})
        .then((e) => {
          if (e && e.data && e.data.animal.create.success) {
            dispatch({type: String(gqlActions.domain.animals.maintained), payload: {con: connection.connection, data: e.data.animal.create.updatedValues}})
          }
        }).catch((error) => {console.log(error)})
    } catch (error) {
      console.log(error);
    }
  },[client, connection.connection, dispatch])

  const getQuickEntryAnimals = async (domain: string, quickEntryFilter: QuickEntry[]) => {
    
    const filters = quickEntryFilter.map((filter) => {
      return {ids: filter.value, key: camelToSnakeCase(filter.key)}
    })

    try {
      return await client.query<FilteredAnimalsResponse>({query: GET_FILTERED_ANIMALS, variables: {domain, filters}}).catch((error) => {
        console.log(error);
        return {data: {animal: {filtered: []}}, loading: false, networkStatus: NetworkStatus.error};
      });
    } catch (error) {
      console.log(error);
      return {data: {animal: {filtered: []}}, loading: false, networkStatus: NetworkStatus.error}
    }
  }

  return {call: getAnimalInfo, fetchMore: noop, deleteEntries: noop, data: fullState, maintainEntries, getAnimalAtLocation, createAnimals, getQuickEntryAnimals}
} 

